/**
 * Lectura del archivo que se sube a la base (SPEC-BD §6.2, paso 1): del `File` del navegador al cuerpo de
 * `POST /base/cargas`.
 *
 * - Un Excel (.xlsx o .xls) viaja en base64, sin el prefijo `data:…;base64,` de `readAsDataURL`: el backend
 *   decodifica el base64 tal cual y con el prefijo no leería nada.
 * - Un CSV viaja como texto. Se lee como UTF-8 y, si aparece el carácter de reemplazo (U+FFFD), se relee
 *   como windows-1252: es lo que guarda Excel en Windows con «CSV (delimitado por comas)», y leído como
 *   UTF-8 cada «ñ» o tilde se convierte en «�» para siempre.
 *
 * El tamaño y el tipo se chequean ANTES de leer (`validarArchivoLocal`): leer 20 MB para después
 * rechazarlos cuelga la pestaña, y un 413 del backend tarda en llegar.
 */

import type { ArchivoCarga, EntradaCarga } from "../types.base";
import { nombreDeCarga, validarArchivoLocal } from "./base";

function leerComo(f: Blob, modo: "dataurl" | "texto", codificacion?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result : "");
    r.onerror = () => reject(new Error("No pudimos leer el archivo. Probá de nuevo o elegilo otra vez."));
    if (modo === "dataurl") r.readAsDataURL(f);
    else r.readAsText(f, codificacion);
  });
}

/**
 * El texto de un CSV: como UTF-8 y, si aparece «�», otra vez como windows-1252. Lo usan también los CSV de pedidos y
 * clientes de las campañas de Activa y del seguimiento, que se leían con `readAsText` a secas.
 */
export async function textoDeCsv(f: Blob): Promise<string> {
  const utf8 = await leerComo(f, "texto", "utf-8");
  return utf8.includes("�") ? leerComo(f, "texto", "windows-1252") : utf8;
}

/**
 * El contenido del archivo como lo espera el backend. Lanza un Error con el texto para el operador si el
 * archivo no es un Excel ni un CSV, si pesa más que el tope, o si el navegador no lo pudo leer.
 */
export async function leerArchivo(f: File): Promise<ArchivoCarga> {
  const v = validarArchivoLocal(f?.name ?? "", f?.size ?? 0);
  if (!v.ok) throw new Error(v.error);
  if (v.tipo === "csv") {
    return { content: await textoDeCsv(f), encoding: "csv" };
  }
  const url = await leerComo(f, "dataurl");
  const coma = url.indexOf(",");
  const content = coma >= 0 ? url.slice(coma + 1) : "";
  // Un Excel vacío o un data URL raro no se manda: el backend contestaría ARCHIVO_VACIO con un archivo que sí tenía algo.
  if (!content) throw new Error("No pudimos leer el archivo. Probá de nuevo o elegilo otra vez.");
  return { content, encoding: "base64" };
}

/**
 * El cuerpo completo de `POST /base/cargas`: el nombre de la carga (el que escribió el operador o, si lo
 * dejó vacío, el del archivo sin extensión; hasta 120), el nombre del archivo (hasta 200) y el contenido.
 */
export async function prepararCarga(f: File, nombre?: string): Promise<EntradaCarga> {
  const archivo = await leerArchivo(f);
  // Se recorta el nombre y no la extensión: el backend decide el formato por la extensión.
  const completo = String(f.name || "").trim();
  const ext = /\.[a-z0-9]+$/i.exec(completo)?.[0] ?? "";
  const nombreArchivo = completo.length <= 200 ? completo : `${completo.slice(0, 200 - ext.length)}${ext}`;
  const elegido = String(nombre ?? "").trim().slice(0, 120);
  return { nombre: elegido || nombreDeCarga(nombreArchivo), nombreArchivo, archivo };
}
